import { getProductPrice } from "../components/orderhistory/vatUtils";
import { EmailProductData } from "../stores/appOrderHistoryStore";

export const isClickablePlatform = (platform: string): boolean => {
  const platformLower = platform.toLowerCase();
  return platformLower === 'bidfood' || platformLower === 'vangelder' || platformLower === 'hanos' || platformLower === 'sligro';
};

export const getSupplierUrl = (platform: string, articleId: string): string => {
  const platformLower = platform.toLowerCase();
  if (platformLower === 'bidfood') {
    return `https://www.bidfood.nl/webshop/catalog/productDetail.jsp?productId=${articleId}`;
  } else if (platformLower === 'vangelder') {
    return `https://www.vangeldernederland.nl/nl_NL/search/?search_term=${articleId}`;
  } else if (platformLower === 'hanos') {
    return `https://www.hanos.nl/nl/Assortiment/p/${articleId}`;
  } else if (platformLower === 'sligro') {
    return `https://www.sligro.nl/search.html?query=${articleId}`;
  }
  return '';
};

export const platformsWithPerKgPrice = ['pastanobile', 'naturalia', 'kaasfort', 'dzw', 'gnb', 'nicetomeat', 'altseafood', 'dekoningvlees', 'rellmanfoods', 'kraan', 'antep', 'fontijn'];

/**
 * Platforms whose products are identified by NAME rather than by article id (see
 * `resolveProductIdentifier`). Almost all of them simply print no article-id column.
 * EXCEPTION — `dorstlust` DOES print a "Nr." per line and stores it verbatim, but reuses the same id
 * for unrelated products (invoice VFDL-0017307 prints 20910 for BOTH "Bouche Mandarin" and "Bouche
 * Cheeerry"), so keying on it would merge distinct products. The stored id is display-only there.
 * Server twin: `server/utils/platformUtils.ts`.
 */
export const platformsWithoutArticleId = ['euroslagerijgrieving', 'hollandvis', 'guillaume', 'staffhorst', 'pastanobile', 'moestuinvollenhoven', 'minzevenijs', 'localharvest', 'loui', 'buonappetito', 'vangelder', 'tonkonijn', 'gebogelato', 'brandingclub', 'eneco', 'wintax', 'eurofins', 'evides', 'kiya', 'koelmonteur', 'upta', 'verisure', 'whiskysite', 'any_invoice', 'rightspirits', 'lindschotendrink', 'exclusivelabelbenelux', 'marckroonwijn', 'grapedistrict', 'sanderswijnkopers', 'bjdelogie', 'claudia', 'damman', 'treuren', 'stadshavenbrouwerij', 'kemmewines', 'jouwgroenteman', 'goldtdrinks', 'jordysbakery', 'patisseriebondsmolders', 'vatex', 'lasalute', 'gioempanadas', 'aapnootbrood', 'deweert', 'freshfood_dh', 'ruyken', 'filta', 'bevenco', 'sebcocktails', 'ilovecannoli', 'alberthein', 'jessedelicatesse', 'purematcha', 'maisonroutin', 'bestbreadboba', 'greenfreshfood', 'degoudenton', 'vulcaanbier', 'nrbc', 'senzatea', 'hoecksevis', 'drankenhandel', 'ceesvannoordwijn', 'frobel', 'ijsenijskoud', 'cavinoir', 'pieksmanwijn', 'deambachterie', 'foodsteps', 'drinksfromjapan', 'dorstlust', 'smitsdelicious', 'westlandpeppers', 'koekela', 'vandebakker', 'kaapsebrouwers', 'stationbergweg', 'sakebase', 'dronk', 'koylu', 'iranbezorger', 'antep', 'baghrir', 'lakarusch', 'fontijn', 'altinpetek', 'quebracho'];

// `westlandpeppers` / `koekela`: the printed total ("Totaal" / "Eindtotaal", and therefore our ex-VAT
// `total`) already contains the "Verzending" line, so subtracting shipping again would understate the
// VAT amount. Dun Yong legacy rows have estimated VAT from lines, so their old shipping field must
// never be subtracted from that VAT (before or after P2 adds it as a line). Retain until field retirement.
export const platformsShippingIncluded = ['vinvin', 'nitrobrew', 'whiskysite', 'hanos', 'vangelder', 'rungis', 'verbuntverlinden', 'banketbakkerij', 'maisonroutin', 'manetti', 'westlandpeppers', 'koekela', 'koffiekompaan', 'dunyong'];

/**
 * Article-id values that are placeholders, NOT product identities. Many adapters write the literal
 * 'no_article_id'; a few LLM extractions emit the string 'null'/'none'/'n/a'. Keying on these collapses
 * every such product into ONE product across the whole platform.
 *
 * This is a per-LINE check, deliberately distinct from `platformsWithoutArticleId` (a per-PLATFORM flag).
 * takeaware/horecagoedkoop/manetti/zeechocolate carry real article ids on their product lines and the
 * sentinel only on surcharge/shipping lines — the platform flag would wrongly discard their real ids.
 *
 * MUST stay in sync with server/utils/platformUtils.ts.
 */
const GLOBAL_NON_IDENTIFYING_ARTICLE_IDS = new Set(['no_article_id', 'null', 'none', 'n/a']);

/**
 * Platform-scoped variants of the above: real-looking article ids the supplier reuses as a bucket for
 * anything that doesn't have one. Verified against production data (each merged 2–8 unrelated products).
 */
const PLATFORM_NON_IDENTIFYING_ARTICLE_IDS: Record<string, string[]> = {
  brouwerijnoordt: ['emballage'], // Distinct keg deposits keep their own product identity.
  kobier: ['div-bier', 'pos', 'stg-div', 'stg-blik'],
  nespresso: ['original'],
  vinites: ['prijs'],
  zeechocolate: ['verzendkosten', 'korting'],
  // treuren's printed "Factuurkorting N%" is distributed over its pakbon orders as negative KORTING
  // lines (factuurData/invoiceKorting.ts via treuren/emailProcessing), so the distinct names must carry
  // the identity, not the shared sentinel.
  treuren: ['korting'],
  // verhoek stores its Poolfust EPS deposits under 'EMBALLAGE' and its invoice-level korting as
  // KORTING lines, so the distinct names (groot/klein, per-bucket labels) carry the identity.
  verhoek: ['emballage', 'korting'],
  // Every deklokdranken emballage/statiegeld line carries the hardcoded articleId 'EMBALLAGE'; without
  // this the resolver would collapse Krat/Fust/Cylinder deposits (different amounts) into ONE product,
  // causing false price alerts + merged inventory. Fall back to the distinct EMB_<name> instead.
  deklokdranken: ['emballage'],
  // Same for driessenfood: every Btw-"E" line (Rood/Blauw/Groen krat, Fust) is stored as 'EMBALLAGE',
  // so the distinct crate names must carry the identity instead.
  driessenfood: ['emballage'],
  bidfood: ['emballage', 'bidfood_correctie', 'verwerkingstoeslag'],
  // gnb's whole-invoice korting is stored as synthetic negative lines under the hardcoded articleId
  // 'KORTING' (see gnb/emailProcessing/emailDataHelper.ts applyInvoiceDiscount), one per VAT rate on a
  // mixed-rate order — so the distinct names must carry the identity, not the shared sentinel.
  gnb: ['korting'],
  // dzw's factuur "Korting N% over Euro <base>" is distributed the same way (server factuurData/invoiceKorting.ts
  // via dzw/emailProcessing) — one KORTING line per bon, plus the dzw korting backfill.
  dzw: ['korting'],
  banketbakkerij: ['korting', 'verzendkosten'],
  aapnootbrood: ['korting'],
  // mariuswijnsake's B2B invoices print a whole-invoice "Kortingspercentage N%", stored as KORTING lines.
  mariuswijnsake: ['korting'],
  bourgogneexpress: ['korting'],
  naturalia: ['korting'],
  dunyong: ['korting', 'verzendkosten'],
  lexception: ['verzendkosten'],
  bartsbottles: ['verzendkosten'],
  drankdozijn: ['verzendkosten'],
  rightspirits: ['verzendkosten'],
  senzatea: ['verzendkosten'],
  any_invoice: ['verzendkosten'],

  // illy's order-confirmation "Verzending en verwerking" line is stored under the sentinel 'VERZENDKOSTEN'
  // (illy/emailProcessing/emailDataHelper.ts); the name carries the identity, like zeechocolate's.
  illy: ['verzendkosten'],
  vinvin: ['verzendkosten'],
  nitrobrew: ['verzendkosten'],
  manetti: ['verzendkosten'],
  // cocktailunlimited's "Bezorging per pakketservice" is appended as the shared VERZENDKOSTEN money
  // line (appendOrderMoneyLine); the platform's real article ids stay identities, this one must not.
  cocktailunlimited: ['verzendkosten'],
  verbuntverlinden: ['verzendkosten'],
  whiskysite: ['verzendkosten'],
  koekela: ['verzendkosten'],
  koffiekompaan: ['verzendkosten'],
  westlandpeppers: ['verzendkosten'],
  maisonroutin: ['verzendkosten'],
  hanos: ['emballage'],
  // vhckreko stores every deposit line (Statiegeld, the Krat lines, Retour emballage) under
  // 'EMBALLAGE' and both "2,0 % Korting laag/hoog % BTW" lines under 'KORTING' (see
  // vhckreko/emailProcessing/emailDataHelper.ts resolveKrekoArticleId), so the distinct crate and
  // korting NAMES must carry the identity instead of the shared sentinels.
  vhckreko: ['emballage', 'korting'],
};

/**
 * "Restgroep" article ids: genuine article numbers a supplier reuses across unrelated products.
 * Unlike the sentinels above these ARE real ids, so we keep the id in the key and disambiguate by name.
 *
 * A rule's `match` is tested against the NORMALIZED name, so it survives LLM/OCR noise and
 * pakbon-vs-factuur naming differences ("Finger Limes 1kg", "Fingerlimes per kg" and "finger limes per
 * kilo" all resolve to one product). Names matching no rule fall back to the normalized name — correct
 * for true junk drawers (54.999.93 holds 53 unrelated items), where splitting per product is what we want.
 *
 * Do NOT add ids here just because their names vary: on every other platform the name variance is noise
 * that articleId keying correctly holds together (e.g. bidfood "HEINZ" vs "TOMATEN KETCHUP").
 */
const CATCH_ALL_ARTICLE_IDS: Record<string, Record<string, Array<{ match: RegExp; alias: string }>>> = {
  vhcjongens: {
    // Watermeloen geel (piece) / Finger Limes (per kg) / Morilles (per kg) share this id.
    '45.100.13': [
      { match: /finger\s*limes?/, alias: 'fingerlimes' },
      { match: /watermeloen/, alias: 'watermeloen' },
      { match: /morille/, alias: 'morilles' },
    ],
    '01.000.68': [], // mirin / pane carasau / flour / Sourcy / Tork holder — junk drawer
    '43.770.17': [], // ready meals: pasta-pesto / gele curry rijst / rot maaltijd
    '54.999.93': [], // 53 unrelated non-catalogue items
  },
};

/**
 * Canonical product name for keying: lowercase, all non-alphanumeric runs collapsed to single spaces.
 * Absorbs the LLM/OCR noise seen in production — leading `,`/`-`/`/`/`!`/`*`, `iDeal` vs `iDEAL`,
 * `20cm 6mm` vs `20cm/6mm`, `JAGGERY GUR (1x900G)` vs `JAGGERY GUR(1X900G)`.
 *
 * Deliberately does NOT strip leading quantity prefixes ("1 st ", "10 KG "): on name-keyed platforms a
 * leading pack size can be the only thing distinguishing two products ("1 KG X" vs "5 KG X"), and those
 * prefixes only occur on article-id-keyed platforms where the name isn't the key anyway.
 */
export function normalizeProductName(name: string | null | undefined): string {
  return (name ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * A leading LINE QUANTITY, e.g. "2 st pasta/pesto/rooms" or "1 st Cutterbox plastic folie". Stripped
 * ONLY inside catch-all buckets (see below), never for platform-wide name keys — there a leading pack
 * size can be the sole difference between two products ("1 KG X" vs "5 KG X").
 */
const LEADING_LINE_QUANTITY = /^\d+(?:[.,]\d+)?\s*(?:stuks|stuk|st|kgs|kg|gram|gr|ltr|lt|ml|cl|rl|g|l)\s+/;

/**
 * Name key used inside a catch-all bucket when no curated alias matches. On vhcjongens' 43.770.17 every
 * line reads "2 st <meal>", so without the strip the same meal at a different quantity becomes a new
 * product. Deliberately NOT keyed on the SKU code some names embed ("B1611 Class." / "B1611 Classi"):
 * detecting that reliably needs exceptions for "2st", "1000ml" and bare numbers, and a wrong guess
 * MERGES unrelated products — the very bug this module exists to prevent. Splitting fails closed.
 */
function normalizeCatchAllName(name: string | null | undefined): string {
  return normalizeProductName(name).replace(LEADING_LINE_QUANTITY, '').trim();
}

/**
 * THE single source of truth for "which product is this line?" — used to group/dedupe/match a product
 * across orders, price alerts, cost-price ingredients, inventory and reports. Every call site that used
 * to branch on `platformsWithoutArticleId.includes(platform)` must use this instead, on BOTH sides of a
 * comparison, so the two sides normalize identically.
 *
 * Resolution order:
 *   1. catch-all article id  → `${articleId}::${curatedAlias ?? normalizedName}`
 *   2. sentinel / blank id, or platform in `platformsWithoutArticleId` → normalized name
 *   3. otherwise → the article id verbatim
 *
 * Returns null when there is no usable identifier at all (blank id AND blank name) — caller should skip
 * the product.
 */
export function resolveProductIdentifier(
  platform: string,
  articleId: string | null | undefined,
  name: string | null | undefined,
): string | null {
  const platformLower = (platform || '').toLowerCase();
  const id = (articleId ?? '').trim();

  // Memoized on the three inputs that fully determine the result. The charts page resolves the same
  // few thousand distinct products millions of times (every ingredient x every order line x every
  // period), and the regex in normalizeProductName dominated the page's CPU time.
  const cacheKey = `${platformLower}\u0000${id}\u0000${name ?? ''}`;
  const cached = identifierCache.get(cacheKey);
  if (cached !== undefined) return cached;

  const resolved = resolveProductIdentifierUncached(platformLower, id, name);
  identifierCache.set(cacheKey, resolved);
  return resolved;
}

// Bounded in practice by the number of distinct (platform, articleId, name) triples the session
// touches — a few thousand per restaurant.
const identifierCache = new Map<string, string | null>();

function resolveProductIdentifierUncached(
  platformLower: string,
  id: string,
  name: string | null | undefined,
): string | null {
  const normalizedName = normalizeProductName(name);

  const catchAllRules = CATCH_ALL_ARTICLE_IDS[platformLower]?.[id];
  if (catchAllRules) {
    const rule = catchAllRules.find(r => r.match.test(normalizedName));
    const suffix = rule ? rule.alias : normalizeCatchAllName(name);
    return suffix ? `${id}::${suffix}` : null;
  }

  const idIsNonIdentifying =
    !id ||
    GLOBAL_NON_IDENTIFYING_ARTICLE_IDS.has(id.toLowerCase()) ||
    (PLATFORM_NON_IDENTIFYING_ARTICLE_IDS[platformLower] ?? []).includes(id.toLowerCase());

  if (idIsNonIdentifying || platformsWithoutArticleId.includes(platformLower)) {
    return normalizedName || null;
  }

  return id;
}

export const calculateProductTotal = (product: EmailProductData, platform: string, showExVat: boolean = true): number => {
  const qty = getProductQuantity(product, platform);
  const price = getProductPrice(product, showExVat);
  return price * qty;
};

/**
 * THE valuation gate: did the supplier actually CHARGE for this line? (price > 0)
 *
 * Suppliers hand out one-off free bottles/samples (price 0) and send credit notes
 * (negative prices). Those lines are REAL invoice data — they stay in spend, order
 * totals, cost%, quantities and factuur reconciliation untouched — but a free line must
 * never become a product's current price. Every latest-price lookup (cost price,
 * inventory, waste/leakage, reports, price alerts) skips them and walks back to the most
 * recent line that did charge.
 *
 * Server twin: `server/utils/platformUtils.ts`.
 */
export function isPricedProductLine(product: { price?: number | string | null } | null | undefined): boolean {
  const price = typeof product?.price === 'number' ? product.price : Number(product?.price);
  return Number.isFinite(price) && price > 0;
}

/** A line carrying ANY non-zero amount — charged or credited. See `selectValuationLine`. */
export function isValuationCandidate(product: { price?: number | string | null } | null | undefined): boolean {
  const price = typeof product?.price === 'number' ? product.price : Number(product?.price);
  return Number.isFinite(price) && price !== 0;
}

/**
 * Pick the line a product should be valued from, walking candidates NEWEST FIRST:
 *
 *  1. the most recent line that actually charged (`isPricedProductLine`), else
 *  2. — only when the product was NEVER charged for — the most recent credit line. Some
 *     products only ever appear negative (a retour crate always gives €3 back); their
 *     magnitude IS the value, and every price-per-std-unit helper already `Math.abs`es it.
 *     A normal product that got credited once is unaffected: its positive history wins.
 *  3. null when every candidate is €0 (genuinely never paid for → callers fall back to
 *     0/null, or to the manual inventory price override).
 *
 * Zeros are never a price. Server twin: `server/utils/platformUtils.ts`.
 */
export function selectValuationLine<T>(
  candidatesNewestFirst: T[],
  priceOf: (candidate: T) => number | string | null | undefined
): T | null {
  let latestCredit: T | null = null;
  for (let i = 0; i < candidatesNewestFirst.length; i++) {
    const candidate = candidatesNewestFirst[i];
    const raw = priceOf(candidate);
    const price = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(price) || price === 0) continue;
    if (price > 0) return candidate;
    if (latestCredit === null) latestCredit = candidate;
  }
  return latestCredit;
}

/**
 * Choose the quantityInfo that converts a valuation line's price into a
 * price-per-std-unit for an ingredient. Normally the LINE's own packaging info —
 * a genuine pack-size change (500 g → 1 kg) must flow through. But when the
 * line's stdUnit CONTRADICTS the unit the ingredient was saved in (e.g. stored
 * as grams, latest factuur mis-parsed as "1 stuk"), the line's price is still
 * the real pack price while its packaging metadata is garbage: divide by the
 * ingredient's stored snapshot instead, so quantityNeeded (expressed in the
 * snapshot's unit) keeps producing a sane cost until the data is fixed.
 * Also falls back to the snapshot when the line carries no usable quantityInfo
 * at all (previously such ingredients silently priced as undefined).
 * Returns null when neither side is usable. Server twin: `server/utils/platformUtils.ts`.
 */
export function resolveValuationQuantityInfo<
  Q extends { stdAmount?: number; stdMultiplier?: number; stdUnit?: string }
>(snapshot: Q | null | undefined, line: Q | null | undefined): Q | null {
  const usable = (qi: Q | null | undefined): qi is Q =>
    !!qi && Number.isFinite(Number(qi.stdAmount)) && Number(qi.stdAmount) > 0 &&
    Number.isFinite(Number(qi.stdMultiplier)) && Number(qi.stdMultiplier) > 0;
  if (!usable(line)) return usable(snapshot) ? snapshot : null;
  if (!usable(snapshot) || !snapshot.stdUnit || !line.stdUnit) return line;
  return snapshot.stdUnit === line.stdUnit ? line : snapshot;
}

/**
 * Get the correct quantity to use for calculations based on platform
 * DZW and GNB use rawQuantity, others use quantity
 */
export function getProductQuantity(product: EmailProductData, platform: string): number {
  const platformName = platform.toLowerCase();
  if (platformsWithPerKgPrice.includes(platformName)) {
    return product.rawQuantity || 0;
  }
  return product.quantity || 0;
}
